import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import type { Player } from "@/lib/types";
import { getCoachingNotes, errorMessage } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { useScopedKey } from "@/lib/queryClient";
import { useScopeStore } from "@/stores/scope";
import { useRoster, useRosterMutations } from "./hooks/useRoster";
import {
  computeRosterKpis,
  filterRoster,
  formatNotesPerPlayer,
  type RosterStatusFilter,
} from "./rosterMetrics";
import { PlayerModal } from "./components/PlayerModal";
import { LinkAccountModal } from "./components/LinkAccountModal";
import { RosterImportModal } from "./components/RosterImportModal";
import { PageHeader } from "./components/PageHeader";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useToast } from "@/components/ui/toast";

/**
 * Coach > Roster list page (M6a).
 *
 * Ports the legacy `renderCoachRoster` surface
 * (`js/coaching/roster.js`): KPI tiles, a search box + status
 * filter, Quick Add, the player table with link chips, and the
 * per-row action set.
 *
 * Row actions:
 *   - **Add observation** → routes to Coach Review on the
 *     tactical-board surface with the player preselected (the
 *     M4→M6 `routeNewObservation` carry-in).
 *   - **View development profile** → 🔵 disabled, "lands in M7" —
 *     the Player Development profile modal is M7 (it shares a
 *     painter with the viewer-side Development sub-tab). The
 *     button is rendered so the entry point is visible.
 *   - **Create player goal** → opens the goal modal, but ONLY
 *     when the parent passes `onCreatePlayerGoal` — the goal
 *     modal lands in the Goals slice, which wires this up. Until
 *     then the button is hidden rather than dead.
 *   - **Link account** → the link-account modal.
 *   - **Edit** / **Delete** → the player modal / a confirm-delete.
 *
 * KPIs reflect the FULL roster (not the filtered view) — legacy
 * behavior. The notes-per-player KPI needs every team note, so
 * this page also reads `getCoachingNotes()` (unfiltered).
 */

export type RosterPageProps = {
  /** Opens the goal composer (create mode) preselected to this
   * player. Undefined ⇒ the row's goal button is hidden. Owned by
   * `CoachShell` so the goal + development modals stay
   * one-at-a-time. */
  onCreatePlayerGoal?: (playerId: string) => void;
  /** Opens the Player Development modal for a player. Owned by
   * `CoachShell` (same one-modal-at-a-time reason). */
  onOpenDevelopment: (playerId: string, playerName: string) => void;
};

const STATUS_FILTERS: ReadonlyArray<{
  value: RosterStatusFilter;
  label: string;
}> = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

export function RosterPage({
  onCreatePlayerGoal,
  onOpenDevelopment,
}: RosterPageProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const teamId = useScopeStore((s) => s.teamId);
  // CSV roster import is team-admin-only (the backend gates both
  // import endpoints on `team_admin`). Gate the launch button on
  // the active scope's effective role so a plain coach never sees
  // a button that would 403.
  const effectiveRole = useScopeStore((s) => s.active?.effective_role);
  const canImportRoster = effectiveRole === "team_admin";
  const rosterQuery = useRoster();
  const mutations = useRosterMutations();

  // The notes-per-player KPI needs every team note (unfiltered).
  const notesQuery = useQuery({
    queryKey: useScopedKey("coaching-notes", { match_id: null }),
    queryFn: () => getCoachingNotes(),
    enabled: !!teamId,
  });

  const players = useMemo(
    () => rosterQuery.data?.players ?? [],
    [rosterQuery.data],
  );
  const notes = useMemo(
    () => notesQuery.data?.notes ?? [],
    [notesQuery.data],
  );
  const kpis = useMemo(
    () => computeRosterKpis(players, notes),
    [players, notes],
  );

  // ---- Search + status filter ----
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<RosterStatusFilter>("all");
  const filtered = useMemo(
    () => filterRoster(players, search, statusFilter),
    [players, search, statusFilter],
  );

  // ---- Modal state ----
  // `playerModal`: null = closed; { player: null } = Quick Add;
  // { player } = edit. `linkModal`: the player being linked.
  const [playerModal, setPlayerModal] = useState<
    { player: Player | null } | null
  >(null);
  const [linkModal, setLinkModal] = useState<Player | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Player | null>(null);

  const handleAddObservation = (playerId: string) => {
    // The M4→M6 carry-in: route to Coach Review's tactical-board
    // surface with the player preselected. Coach Review reads
    // `?source` + `?player_id` from the search params.
    navigate({
      to: "/coach/review",
      search: { source: "tactical_board", player_id: playerId },
    });
  };

  const handleDelete = (player: Player) => {
    setConfirmDelete(player);
  };

  const confirmDeletePlayer = async () => {
    if (!confirmDelete) return;
    const player = confirmDelete;
    setConfirmDelete(null);
    setDeleting(player.id);
    try {
      await mutations.deletePlayer(player.id);
      toast.success("Player deleted");
    } catch (err) {
      toast.error("Couldn't delete player", { description: errorMessage(err) });
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="flex flex-col gap-6" data-testid="roster-page">
      {confirmDelete ? (
        <ConfirmDialog
          title="Delete player?"
          message={`Delete ${confirmDelete.display_name}? This removes the player and their account links.`}
          confirmLabel="Delete"
          danger
          onConfirm={confirmDeletePlayer}
          onClose={() => setConfirmDelete(null)}
          data-testid="roster-delete-confirm"
        />
      ) : null}
      <PageHeader
        title="Roster & Family Links"
        description="Manage players on the team, jersey numbers, and the family or player accounts that can see private feedback."
        actions={
          canImportRoster ? (
            <button
              type="button"
              onClick={() => setImportOpen(true)}
              data-testid="roster-import-open"
              className="border border-rule text-ink hover:bg-surface rounded px-4 py-1.5 text-sm font-display uppercase tracking-wide transition-colors"
            >
              Import CSV
            </button>
          ) : undefined
        }
      />

      {/* KPI tiles — reflect the full roster, not the filtered view. */}
      <div
        className="grid grid-cols-2 gap-3 lg:grid-cols-4"
        data-testid="roster-kpis"
      >
        <KpiTile label="Active Players" value={String(kpis.activePlayers)} />
        <KpiTile
          label="Linked Accounts"
          value={String(kpis.linkedAccounts)}
          delta={
            kpis.linkedAccountsThisWeek > 0
              ? `+${kpis.linkedAccountsThisWeek} this week`
              : undefined
          }
        />
        <KpiTile
          label="Without Family Link"
          value={String(kpis.withoutLink)}
        />
        <KpiTile
          label="Avg. Notes / Player"
          value={formatNotesPerPlayer(kpis.notesPerPlayer)}
        />
      </div>

      {/* Toolbar: search + status filter + Quick Add. */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, jersey, position, linked account…"
          data-testid="roster-search"
          className="flex-1 min-w-[14rem] bg-bg-card border border-rule rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
        />
        <div
          className="flex gap-1 rounded-md border border-rule bg-bg-card p-1"
          role="group"
          aria-label="Status filter"
        >
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setStatusFilter(f.value)}
              data-roster-filter={f.value}
              data-active={statusFilter === f.value}
              className={[
                "rounded px-3 py-1 text-xs font-display uppercase tracking-wide transition-colors",
                statusFilter === f.value
                  ? "bg-accent text-white"
                  : "text-ink-muted hover:text-ink",
              ].join(" ")}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setPlayerModal({ player: null })}
          data-testid="roster-quick-add"
          className="bg-accent hover:bg-accent-hover text-white rounded px-4 py-1.5 text-sm font-display uppercase tracking-wide transition-colors"
        >
          + Quick Add
        </button>
      </div>

      {/* Player table. */}
      {rosterQuery.isLoading ? (
        <p
          className="text-ink-muted text-sm font-mono px-3 py-6"
          data-testid="roster-loading"
        >
          Loading roster…
        </p>
      ) : players.length === 0 ? (
        <p
          className="text-ink-muted text-sm px-3 py-6"
          data-testid="roster-empty"
        >
          No roster players yet. Use <strong>Quick Add</strong> to add the
          first one.
        </p>
      ) : filtered.length === 0 ? (
        <p
          className="text-ink-muted text-sm px-3 py-6"
          data-testid="roster-no-match"
        >
          No players match your search or filter.{" "}
          <button
            type="button"
            onClick={() => {
              setSearch("");
              setStatusFilter("all");
            }}
            className="text-accent hover:underline"
          >
            Clear filters
          </button>
        </p>
      ) : (
        <table
          className="w-full text-sm"
          data-testid="roster-table"
        >
          <thead>
            <tr className="text-left text-ink-muted text-xs font-display uppercase tracking-wider border-b border-rule">
              <th className="px-2 py-2 w-12">#</th>
              <th className="px-2 py-2">Player</th>
              <th className="px-2 py-2">Linked accounts</th>
              <th className="px-2 py-2 w-24">Status</th>
              <th className="px-2 py-2 w-px whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <RosterRow
                key={p.id}
                player={p}
                deleting={deleting === p.id}
                onAddObservation={() => handleAddObservation(p.id)}
                onOpenDevelopment={() =>
                  onOpenDevelopment(p.id, p.display_name)
                }
                onCreateGoal={
                  onCreatePlayerGoal
                    ? () => onCreatePlayerGoal(p.id)
                    : undefined
                }
                onLink={() => setLinkModal(p)}
                onUnlink={async (linkId) => {
                  try {
                    await mutations.unlinkAccount(linkId);
                    toast.success("Account unlinked");
                  } catch (err) {
                    toast.error("Couldn't unlink account", {
                      description: errorMessage(err),
                    });
                  }
                }}
                onEdit={() => setPlayerModal({ player: p })}
                onDelete={() => handleDelete(p)}
              />
            ))}
          </tbody>
        </table>
      )}

      {playerModal ? (
        <PlayerModal
          player={playerModal.player}
          mutations={mutations}
          onClose={() => setPlayerModal(null)}
        />
      ) : null}
      {linkModal ? (
        <LinkAccountModal
          player={linkModal}
          mutations={mutations}
          onClose={() => setLinkModal(null)}
        />
      ) : null}
      {importOpen ? (
        <RosterImportModal onClose={() => setImportOpen(false)} />
      ) : null}
    </div>
  );
}

function KpiTile(props: { label: string; value: string; delta?: string }) {
  return (
    <div className="rounded-lg border border-rule bg-bg-card px-4 py-3">
      <div className="text-ink-muted text-xs font-display uppercase tracking-wider">
        {props.label}
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <strong className="text-2xl font-display text-ink tabular-nums">
          {props.value}
        </strong>
        {props.delta ? (
          <span className="text-xs text-accent">{props.delta}</span>
        ) : null}
      </div>
    </div>
  );
}

type RosterRowProps = {
  player: Player;
  deleting: boolean;
  onAddObservation: () => void;
  /** Opens the M7 Player Development profile modal for this player. */
  onOpenDevelopment: () => void;
  /** Undefined ⇒ the goal button is hidden (Goals slice wires it). */
  onCreateGoal?: () => void;
  onLink: () => void;
  onUnlink: (linkId: number) => void;
  onEdit: () => void;
  onDelete: () => void;
};

function RosterRow({
  player,
  deleting,
  onAddObservation,
  onOpenDevelopment,
  onCreateGoal,
  onLink,
  onUnlink,
  onEdit,
  onDelete,
}: RosterRowProps) {
  const links = player.links ?? [];
  return (
    <tr
      className="border-b border-rule/50"
      data-testid="roster-row"
      data-player-id={player.id}
    >
      <td className="px-2 py-2">
        <span className="inline-flex h-6 min-w-6 items-center justify-center rounded bg-surface px-1 text-xs font-mono text-ink">
          {player.jersey_number || "—"}
        </span>
      </td>
      <td className="px-2 py-2">
        <div className="font-display text-ink">{player.display_name}</div>
        {player.notes?.trim() ? (
          <div className="text-ink-muted text-xs">{player.notes.trim()}</div>
        ) : null}
      </td>
      <td className="px-2 py-2">
        {links.length === 0 ? (
          <span className="text-ink-muted text-xs">No links</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {links.map((l) => (
              <button
                key={l.id}
                type="button"
                onClick={() => onUnlink(l.id)}
                data-testid="roster-link-chip"
                title={`Unlink @${l.username} — ${l.relationship}`}
                className="inline-flex items-center gap-1 rounded-full border border-rule bg-surface px-2 py-0.5 text-xs text-ink hover:border-destructive hover:text-destructive transition-colors"
              >
                <span className="text-ink-muted">{l.relationship}</span>
                <span>@{l.username}</span>
                <span aria-hidden="true">×</span>
              </button>
            ))}
          </div>
        )}
      </td>
      <td className="px-2 py-2">
        <span
          className={[
            "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs",
            player.active
              ? "bg-accent/15 text-ink"
              : "bg-surface text-ink-muted",
          ].join(" ")}
        >
          {player.active ? "Active" : "Inactive"}
        </span>
      </td>
      <td className="px-2 py-2">
        <div className="flex items-center gap-1">
          <RowButton
            label="Add observation"
            testid="roster-action-observation"
            onClick={onAddObservation}
          />
          <RowButton
            label="Development profile"
            testid="roster-action-development"
            onClick={onOpenDevelopment}
          />
          {onCreateGoal ? (
            <RowButton
              label="Create goal"
              testid="roster-action-goal"
              onClick={onCreateGoal}
            />
          ) : null}
          <RowButton
            label="Link account"
            testid="roster-action-link"
            onClick={onLink}
          />
          <RowButton
            label="Edit"
            testid="roster-action-edit"
            onClick={onEdit}
          />
          <RowButton
            label="Delete"
            testid="roster-action-delete"
            onClick={onDelete}
            disabled={deleting}
            danger
          />
        </div>
      </td>
    </tr>
  );
}

function RowButton(props: {
  label: string;
  testid: string;
  onClick?: () => void;
  disabled?: boolean;
  danger?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.disabled}
      data-testid={props.testid}
      title={props.title ?? props.label}
      aria-label={props.label}
      className={[
        "rounded border px-2 py-1 text-xs transition-colors disabled:opacity-40",
        props.danger
          ? "border-rule text-ink-muted hover:border-destructive hover:text-destructive"
          : "border-rule text-ink-muted hover:text-ink hover:bg-surface",
      ].join(" ")}
    >
      {props.label}
    </button>
  );
}
